import { supabase } from '../lib/supabase'

export const ordersService = {
  // Get user's purchase orders with expanded details
  async getUserOrders(userId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(
            id,
            price_at_time,
            quantity,
            artwork:artworks(
              id,
              title,
              images,
              price,
              artist:users!artist_id(
                id,
                full_name
              )
            )
          ),
          order_sellers(
            *,
            seller:users!seller_id(
              id,
              full_name
            )
          )
        `)
        .eq('buyer_id', userId)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get artist's sales orders
  async getArtistSales(artistId) {
    try {
      // First get all artworks for this artist
      const { data: artworks, error: artworksError } = await supabase
        .from('artworks')
        .select('id, title, images')
        .eq('artist_id', artistId);
      
      if (artworksError) throw artworksError;
      
      if (!artworks || artworks.length === 0) {
        return { data: [], error: null };
      }
      
      // Get artwork IDs
      const artworkIds = artworks.map(artwork => artwork.id);
      
      // Now get order items for these artworks
      const { data, error } = await supabase
        .from('order_items')
        .select(`
          *,
          order:orders(
            id,
            order_number,
            status,
            payment_method,
            payment_status,
            mercadopago_payment_id,
            shipped_at,
            delivered_at,
            created_at,
            total_amount,
            buyer:users!buyer_id(
              id,
              full_name
            )
          )
        `)
        .in('artwork_id', artworkIds)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      
      // Enhanced debug logging for Supabase queries
      console.log('🔍 [OrdersService] getArtistSales - artistId:', artistId);
      console.log('🔍 [OrdersService] getArtistSales - artworks found:', artworks?.length);
      console.log('🔍 [OrdersService] getArtistSales - artwork IDs:', artworkIds);
      console.log('🔍 [OrdersService] getArtistSales - order_items found:', data?.length);
      
      if (error) {
        console.error('❌ [OrdersService] Supabase query error:', error);
        console.error('❌ [OrdersService] Error details:', error.details || error.message);
        console.error('❌ [OrdersService] Error hint:', error.hint);
        console.error('❌ [OrdersService] Full error object:', error);
      }
      
      console.log('🔍 [OrdersService] getArtistSales - sample order_item:', data?.[0]);
      
      // Map artwork data to order items
      const dataWithArtworks = data?.map(orderItem => {
        const artwork = artworks.find(a => a.id === orderItem.artwork_id);
        return {
          ...orderItem,
          artwork
        };
      }) || [];
      
      // Sort by order creation date (most recent first)
      const sortedData = dataWithArtworks.sort((a, b) => {
        const dateA = new Date(a.order?.created_at || a.created_at);
        const dateB = new Date(b.order?.created_at || b.created_at);
        return dateB - dateA; // Descending order (newest first)
      });
      
      return { data: sortedData, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get order by ID
  async getOrderById(orderId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(
            *,
            artwork:artworks(
              id,
              title,
              images,
              artist:users!artist_id(
                id,
                full_name
              )
            )
          ),
          buyer:users!buyer_id(
            id,
            full_name
          )
        `)
        .eq('id', orderId)
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Update shipping status (for artists)
  async updateShippingStatus(orderId, updates) {
    try {
      const allowedUpdates = {
        status: updates.status,
        shipped_at: updates.shipped_at,
        delivered_at: updates.delivered_at,
        shipping_tracking_number: updates.shipping_tracking_number
      }

      // Remove undefined values
      const cleanUpdates = Object.fromEntries(
        Object.entries(allowedUpdates).filter(([_, value]) => value !== undefined)
      )

      const { data, error } = await supabase
        .from('orders')
        .update(cleanUpdates)
        .eq('id', orderId)
        .select()
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Update shipping address (for buyers, before shipment)
  async updateShippingAddress(orderId, shippingAddress) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          shipping_address: shippingAddress
        })
        .eq('id', orderId)
        .select()
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get orders by payment status (for admin/reports)
  async getOrdersByPaymentStatus(paymentStatus) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(
            *,
            artwork:artworks(
              id,
              title,
              artist:users!artist_id(
                id,
                full_name
              )
            )
          ),
          buyer:users!buyer_id(
            id,
            full_name
          )
        `)
        .eq('payment_status', paymentStatus)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  }
}